'use client';

import { FC, useId, useState } from 'react';
import {
  Wrapper,
  SmallImagesBox,
  PhotoDetailModal,
  MainSwiper,
  ThumbsSwiper,
  ThumbImage,
  ModalHead,
  YouTubeSwiper,
  ModalContent,
  StyledModal,
  NotFoundBox,
  YouTubeContainer,
  YouTubeBox,
} from './styled';
import { Swiper as SwiperType } from 'swiper';
import YouTube from 'react-youtube';
import { MediaFile, ImageWithSizes, IHousingObjectResponse } from '@/types';
import { useTranslations } from 'next-intl';
import { Button, Modal, Typography } from '@mui/material';
import { CrossIcon } from '@/components';
import { Keyboard, Navigation, Thumbs } from 'swiper/modules';
import { SwiperSlide } from 'swiper/react';
import { COLORS } from '@/constants';
import { extractYouTubeIds } from '@/common';

import { SmallImage } from './SmallImage';
import { MainImage } from './MainImage';
import { MainSwiperSlide } from './MainSwiperSlide';

const opts = {
  height: '467px',
  width: '100%',
  playerVars: {
    autoplay: 0,
  },
};

type MediaBlockProps = {
  data: IHousingObjectResponse;
  mediaLinks?: string[];
  image360?: MediaFile[];
  tourLinks: MediaFile[];
  objectId?: string;
  hasInfoRequest?: boolean;
};

export const Gallery: FC<MediaBlockProps> = ({
  data,
  mediaLinks = [],
  tourLinks = [],
  image360 = [],
  objectId,
  hasInfoRequest,
}: MediaBlockProps) => {
  const images = data.imageList?.length
    ? data.imageList
    : (data.media?.image ?? [])
        .filter((img) => img.link)
        .map((img) => ({
          imageFullUrl: img.link,
          imageMidUrl: img.link,
          imageTmbUrl: img.link,
        }));
  const id = useId();

  const [openVideoModal, setOpenVideoModal] = useState(false);
  const [activePhotoIndex, setActivePhotoIndex] = useState(0);
  const [openPhotoDetailModal, setOpenPhotoDetailModal] = useState(false);
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperType | null>(null);

  const t = useTranslations();

  const videoIds = extractYouTubeIds(mediaLinks);
  const hasVideo = videoIds?.length > 0;
  const photosCount = images.length;
  const hasMultipleImages = photosCount > 1;

  const handleOpenVideoModal = () => {
    setOpenVideoModal(true);
  };

  const handleCloseVideoModal = () => {
    setOpenVideoModal(false);
  };

  const handleOpenPhotoDetail = (index: number) => {
    setOpenPhotoDetailModal(true);
    setActivePhotoIndex(index);
    document.body.style.overflow = 'hidden';
  };
  const handleClosePhotoDetail = () => {
    setThumbsSwiper(null);
    setOpenPhotoDetailModal(false);
  };

  const handleOpenVideoQuiet: React.MouseEventHandler<HTMLButtonElement | HTMLDivElement> = (e) => {
    e.preventDefault();
    e.stopPropagation();
    handleOpenVideoModal();
  };

  const renderSmallImages = () => {
    if (!images.length) return null;

    const smallImagesToShow = images.slice(1, 5);
    const lastImageIndex = smallImagesToShow.length - 1;

    return smallImagesToShow.map((img: ImageWithSizes, index: number) => {
      const isLastImage = index === lastImageIndex;
      const isVideoPlaceholder = index === lastImageIndex - 1 && hasVideo;
      const showMoreButton = isLastImage;

      return (
        <SmallImage
          isVideoPlaceholder={isVideoPlaceholder}
          onOpenVideoQuiet={handleOpenVideoQuiet}
          onOpenPhotoDetail={handleOpenPhotoDetail}
          showMoreButton={showMoreButton}
          hasVideo={hasVideo}
          index={index}
          key={index}
          photosCount={Math.max(0, photosCount - 5)}
          src={img?.imageMidUrl || img?.imageFullUrl || ''}
        />
      );
    });
  };

  return (
    <>
      <Wrapper>
        <MainImage
          hasVideo={hasVideo}
          onOpenVideoQuiet={handleOpenVideoQuiet}
          onOpenPhotoDetail={handleOpenPhotoDetail}
          hasMultipleImages={hasMultipleImages}
          src={images[0]?.imageMidUrl || images[0]?.imageFullUrl}
          tourLinks={tourLinks}
          image360={image360}
          hasInfoRequest={hasInfoRequest}
          objectId={objectId}
        />

        {hasMultipleImages && <SmallImagesBox>{renderSmallImages()}</SmallImagesBox>}
      </Wrapper>

      <StyledModal open={openVideoModal} onClose={handleCloseVideoModal}>
        <ModalContent>
          <ModalHead>
            <Typography fontSize={24} fontWeight={600} lineHeight="34px">
              {t('housingCardDetails.videoTour')}
            </Typography>
            <Button variant="transparent" onClick={handleCloseVideoModal} aria-label="Close">
              <CrossIcon aria-hidden="true" />
            </Button>
          </ModalHead>
          {videoIds.length > 0 ? (
            <YouTubeContainer>
              <YouTubeSwiper
                spaceBetween={20}
                slidesPerView={1}
                navigation={{
                  nextEl: `.YouTube-next`,
                  prevEl: `.YouTube-prev`,
                }}
                pagination={{ clickable: true }}
                modules={[Navigation, Keyboard]}
              >
                {videoIds.map((id) => (
                  <SwiperSlide key={id}>
                    <YouTubeBox>
                      <YouTube videoId={id} opts={opts} style={{ width: '100%', height: '100%' }} />
                    </YouTubeBox>
                  </SwiperSlide>
                ))}
                <button className={`custom-prev YouTube-prev`} aria-label="Previous slide">
                  <span aria-hidden="true">‹</span>
                </button>
                <button className={`custom-next YouTube-next`} aria-label="Next slide">
                  <span aria-hidden="true">›</span>
                </button>
              </YouTubeSwiper>
            </YouTubeContainer>
          ) : (
            <NotFoundBox>
              <Typography
                fontSize={24}
                fontWeight={500}
                lineHeight="34px"
                color={COLORS['primary/900']}
              >
                {`— ${t('housingCardDetails.videoIsNotFound')} —`}
              </Typography>
            </NotFoundBox>
          )}
          <Button
            variant="primary"
            sx={{
              width: '171px',
              height: '44px',
              marginLeft: 'auto',
            }}
            onClick={handleCloseVideoModal}
          >
            {t('housingCardDetails.ok')}
          </Button>
        </ModalContent>
      </StyledModal>

      {photosCount > 0 && (
        <Modal
          open={openPhotoDetailModal}
          onClose={handleClosePhotoDetail}
          sx={{
            '& .MuiBackdrop-root': {
              backgroundColor: 'rgba(0,0,0,0.95)',
            },
          }}
        >
          <PhotoDetailModal>
            <ModalHead>
              <Typography fontSize={34} fontWeight={600} color={COLORS['white']}>
                {`${activePhotoIndex + 1}/${images.length}`}
              </Typography>

              <Button variant="transparent" onClick={handleClosePhotoDetail} aria-label="Close">
                <CrossIcon aria-hidden="true" />
              </Button>
            </ModalHead>

            <MainSwiper
              spaceBetween={10}
              navigation={{
                nextEl: `.${id}-next`,
                prevEl: `.${id}-prev`,
              }}
              thumbs={{ swiper: thumbsSwiper }}
              keyboard={{ enabled: true }}
              modules={[Navigation, Thumbs, Keyboard]}
              onSlideChange={(swiper) => setActivePhotoIndex(swiper.activeIndex)}
              initialSlide={activePhotoIndex}
              className="housing-gallery-swiper"
              key={images.length}
            >
              {images?.map((img, index) => (
                <SwiperSlide key={`detail-${index}`}>
                  <MainSwiperSlide src={img.imageFullUrl} alt={`Detail ${index + 1}`} />
                </SwiperSlide>
              ))}

              <button className={`custom-prev ${id}-prev`} aria-label="Previous slide">
                <span aria-hidden="true">‹</span>
              </button>
              <button className={`custom-next ${id}-next`} aria-label="Next slide">
                <span aria-hidden="true">›</span>
              </button>
            </MainSwiper>

            {hasMultipleImages && (
              <ThumbsSwiper
                onSwiper={(swiper: SwiperType) => setThumbsSwiper(swiper)}
                onBeforeDestroy={() => setThumbsSwiper(null)}
                spaceBetween={10}
                slidesPerView={6}
                watchSlidesProgress
              >
                {images.map((img, index) => (
                  <SwiperSlide key={`thumb-${index}`}>
                    <ThumbImage
                      src={(() => {
                        if (
                          img.imageTmbUrl &&
                          img.imageTmbUrl.trim() !== '' &&
                          img.imageTmbUrl !== img.imageFullUrl
                        ) {
                          return img.imageTmbUrl;
                        }
                        return img.imageFullUrl;
                      })()}
                      alt={`Thumb ${index + 1}`}
                    />
                  </SwiperSlide>
                ))}
              </ThumbsSwiper>
            )}
          </PhotoDetailModal>
        </Modal>
      )}
    </>
  );
};
